import React from 'react';
import { Breadcrumb } from './DemoSiteLayout';
import { T, HAIRLINE, SURFACE } from './siteUI';

/* =========================================================
   Legal document page.

   Renders one document from privacyContent.js / termsContent.js, both of
   which are generated from the .docx by the converter — see the header of
   those files. Nothing here rewrites or summarises the text; this is layout
   only, so the published wording always matches the approved copy.

   Deliberately not dangerouslySetInnerHTML: the source is parsed into typed
   blocks, so a change to the document can never inject markup into the site.
   ========================================================= */

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/* The site runs under HashRouter, so an href="#section" fights the router.
   Scroll directly instead. */
const jumpTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const Block = ({ block }) => {
  switch (block.t) {
    case 'h2':
      return (
        <h2 id={slugify(block.text)} className={`mt-12 scroll-mt-24 first:mt-0 ${T.h3}`}>
          {block.text}
        </h2>
      );

    case 'h3':
      return <h3 className="mt-7 text-[15px] font-semibold text-slate-800">{block.text}</h3>;

    case 'ul':
      return (
        <ul className="mt-3 space-y-2 pl-5">
          {block.items.map((item) => (
            <li key={item} className="list-disc text-[14px] leading-[1.7] text-slate-600 marker:text-teal-500">
              {item}
            </li>
          ))}
        </ul>
      );

    case 'table':
      /* The Notice at Collection table. Scrolls inside its own container so a
         narrow screen never scrolls the whole page sideways. */
      return (
        <div className={`mt-5 overflow-x-auto rounded-2xl bg-white ${HAIRLINE}`}>
          <table className="w-full min-w-[520px] border-collapse text-left">
            {block.head && (
              <thead>
                <tr className="bg-slate-50/80">
                  {block.head.map((cell) => (
                    <th key={cell} className="px-5 py-3 text-[12.5px] font-semibold text-slate-700">
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {block.rows.map((row) => (
                <tr key={row.join('|')} className="border-t border-slate-100 align-top">
                  {row.map((cell, i) => (
                    <td
                      key={cell + i}
                      className={`px-5 py-3.5 text-[13px] leading-[1.6] ${
                        i === 0 ? 'font-semibold text-slate-700' : 'text-slate-600'
                      }`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      /* whitespace-pre-line keeps the line breaks in the contact address. */
      return <p className="mt-3.5 whitespace-pre-line text-[14px] leading-[1.7] text-slate-600">{block.text}</p>;
  }
};

const DemoSiteLegal = ({ doc }) => {
  const sections = doc.blocks.filter((b) => b.t === 'h2').map((b) => b.text);

  return (
    <>
      <Breadcrumb trail={['Home', doc.title]} />

      <header className="py-8">
        <h1 className={T.display}>{doc.title}</h1>
        {doc.meta && <p className={`mt-3 ${T.micro}`}>{doc.meta}</p>}
      </header>

      <div className="grid grid-cols-1 gap-10 pb-8 lg:grid-cols-[220px_minmax(0,1fr)]">
        {/* Contents. A 24-section document is unusable without one. */}
        <nav aria-label="Sections" className="hidden lg:block">
          <div className="sticky top-24">
            <p className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-slate-400">Contents</p>
            <ol className="mt-3 space-y-1.5">
              {sections.map((text) => (
                <li key={text}>
                  <button
                    type="button"
                    onClick={() => jumpTo(slugify(text))}
                    className="rounded text-left text-[12.5px] leading-snug text-slate-500 transition-colors hover:text-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                  >
                    {text}
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </nav>

        <article className={`mx-auto w-full max-w-[74ch] px-6 py-8 sm:px-10 ${SURFACE.band}`}>
          {doc.blocks.map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </article>
      </div>
    </>
  );
};

export default DemoSiteLegal;
